// src/pages/Services.tsx
import { useState } from 'react';
import { Palette, Users, Home, Package, ChevronDown, ChevronUp } from 'lucide-react';

const Services = () => {
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  const services = [
    {
      icon: Palette,
      title: 'Slikanje po naročilu',
      description: 'Unikatne slike, popolnoma prilagojene vašim željam, prostoru in osebnosti. Od portretov do abstraktnih del.',
      features: [
        'Osebni posvet pred začetkom',
        'Različne tehnike (olje, akril, akvarel)',
        'Prilagojene velikosti',
        'Možnost ogleda med procesom'
      ],
      price: 'Od 200€ naprej'
    },
    {
      icon: Users,
      title: 'Slikarske delavnice',
      description: 'Ustvarjalne delavnice za vse starosti in nivoje znanja. Odkrijte umetnost v sproščenem in navdihujočem okolju.',
      features: [
        'Delavnice za otroke (6-14 let)',
        'Delavnice za odrasle',
        'Zasebne ure',
        'Skupinske delavnice'
      ],
      price: 'Od 25€/uro'
    },
    {
      icon: Home,
      title: 'Svetovanje za notranjo opremo',
      description: 'Strokovno svetovanje za izbiro in postavitev umetniških del v vašem prostoru.',
      features: [
        'Analiza prostora',
        'Priporočila za umetniška dela',
        'Izbira barv in stilov',
        'Koordinacija z oblikovalci'
      ],
      price: 'Od 80€/posvet'
    },
    {
      icon: Package,
      title: 'Unikatni izdelki',
      description: 'Ročno izdelani umetniški predmeti: keramika, tekstilni izdelki in umetniški printi.',
      features: [
        'Keramični izdelki',
        'Umetniški printi',
        'Tekstilni izdelki',
        'Darila po meri'
      ],
      price: 'Od 15€ naprej'
    }
  ];

  const faqs = [
    {
      question: 'Koliko časa traja izdelava slike po naročilu?',
      answer: 'Odvisno od velikosti in kompleksnosti dela, običajno med 2-6 tednov. Pri prvem posvetu se dogovorimo za realen rok.'
    },
    {
      question: 'Ali potrebujem predznanje za delavnice?',
      answer: 'Ne! Naše delavnice so primerne za vse nivoje, od popolnih začetnikov do izkušenih slikarjev. Vsak najde svoj izziv.'
    },
    {
      question: 'Ali zagotavljate materiale za delavnice?',
      answer: 'Da, vsi potrebni materiali so vključeni v ceno delavnice. Prinesete le dobre volje in ustvarjalno energijo!'
    },
    {
      question: 'Kako poteka posvet za notranjo opremo?',
      answer: 'Najprej si ogledam vaš prostor, nato skupaj oblikujeva koncept in predlagam konkretna umetniška dela, ki se najbolje vključijo.'
    },
    {
      question: 'Ali lahko naročim darilo za nekoga drugega?',
      answer: 'Seveda! Lahko organiziramo presenetljivo delavnico ali ustvarimo personalizirano darilo. Kontaktirajte nas za ideje.'
    }
  ];

  const toggleFaq = (index: number) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  return (
    <div className="min-h-screen py-8">
      <div className="container mx-auto px-6 lg:px-12">
        {/* Hero Section */}
        <section className="text-center mb-12">
          <div className="max-w-4xl mx-auto">
            <h1 
              className="text-5xl md:text-7xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '0.5rem'
              }}
            >
              Storitve
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Odkrijte pestro paleto umetniških storitev, prilagojenih vašim potrebam
            </p>
          </div>
        </section>

        {/* Services Grid */}
        <section className="mb-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <div 
                  key={service.title}
                  className="glass-card-hover p-8"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <div className="flex items-center mb-6">
                    <div className="w-16 h-16 rounded-full bg-linear-to-br from-purple-400/30 to-pink-400/30 flex items-center justify-center mr-4">
                      <Icon className="w-8 h-8 text-white" />
                    </div>
                    <h2 className="text-2xl font-lora font-bold text-white">
                      {service.title}
                    </h2>
                  </div>
                  
                  <p className="text-white/80 leading-relaxed mb-6">
                    {service.description}
                  </p>

                  <ul className="space-y-2 mb-6">
                    {service.features.map((feature, featureIndex) => (
                      <li key={featureIndex} className="flex items-center text-white/70">
                        <span className="w-2 h-2 bg-purple-400 rounded-full mr-3"></span>
                        {feature}
                      </li>
                    ))}
                  </ul>

                  <div className="flex items-center justify-between">
                    <span 
                      className="text-lg font-semibold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
                      style={{ 
                        lineHeight: 1.2,
                        paddingBottom: 0,
                        marginBottom: 0
                      }}
                    >
                      {service.price}
                    </span>
                    <button className="glass-card px-6 py-2 text-white hover:bg-white/20 transition-all duration-300 hover:scale-105">
                      Povprašaj
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        {/* FAQ Section */}
        <section className="mb-20">
          <div className="text-center mb-16">
            <h2 
              className="text-4xl md:text-5xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Pogosto zastavljena vprašanja
            </h2>
            <p className="text-xl text-white/80 max-w-3xl mx-auto">
              Odgovori na najpogostejša vprašanja o naših storitvah
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="glass-card overflow-hidden">
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-white/5 transition-colors"
                  aria-expanded={openFaq === index}
                >
                  <span className="text-lg font-medium text-white pr-4">
                    {faq.question}
                  </span>
                  {openFaq === index ? (
                    <ChevronUp className="w-5 h-5 text-purple-400 shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-purple-400 shrink-0" />
                  )}
                </button>
                {openFaq === index && (
                  <div className="px-6 pb-4">
                    <p className="text-white/80 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="text-center">
          <div className="glass-card p-12 rounded-3xl max-w-4xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl font-lora font-bold bg-linear-to-r from-purple-400 via-pink-400 to-purple-600 bg-clip-text text-transparent"
              style={{ 
                lineHeight: 1.2,
                paddingBottom: '0.1em',
                marginBottom: '1.5rem'
              }}
            >
              Pripravljeni za umetniško pustolovščino?
            </h2>
            <p className="text-xl text-white/80 mb-8 leading-relaxed">
              Kontaktirajte nas za brezplačen posvet ali se prijavite na delavnico
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="glass-card-hover px-8 py-4 text-lg font-medium text-white">
                Rezerviraj termin
              </button>
              <button className="glass-card px-8 py-4 text-lg font-medium text-white hover:bg-white/20 transition-all duration-300 border-2 border-purple-400/50">
                Pošlji povpraševanje
              </button>
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Services;